/**
 * Icon System for Dashboard
 *
 * Provides icons for:
 * - Traffic sources (X, Google, Facebook, etc.)
 * - Browsers (Chrome, Safari, Firefox, Edge)
 * - Operating systems
 * - Devices
 * - Country flags
 */

import React from 'react'

// =============================================
// SVG Icon Components
// =============================================

export function XIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

export function GoogleIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

export function FacebookIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

export function InstagramIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <defs>
        <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFDC80" />
          <stop offset="25%" stopColor="#FCAF45" />
          <stop offset="50%" stopColor="#F77737" />
          <stop offset="75%" stopColor="#F56040" />
          <stop offset="100%" stopColor="#FD1D1D" />
        </linearGradient>
      </defs>
      <path
        fill="url(#instagram-gradient)"
        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
      />
    </svg>
  )
}

export function YouTubeIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FF0000">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

export function LinkedInIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#0A66C2">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

export function TikTokIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  )
}

export function RedditIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FF4500">
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
    </svg>
  )
}

// Browser Icons
export function ChromeIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" fill="#4285F4" />
      <circle cx="12" cy="12" r="4" fill="#fff" />
      <path fill="#34A853" d="M12 6l6.93 4v8H5.07V10z" opacity="0.8" />
      <path fill="#FBBC05" d="M12 6L5.07 10l6.93 12L22 10z" opacity="0.8" />
      <path fill="#EA4335" d="M5.07 10L12 22l6.93-12H5.07z" opacity="0.8" />
      <circle cx="12" cy="12" r="4" fill="#fff" />
    </svg>
  )
}

export function SafariIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="11" fill="#006CFF" />
      <circle cx="12" cy="12" r="9" fill="#fff" />
      <path fill="#FF3B30" d="M12 12L8 16l8-8z" />
      <path fill="#006CFF" d="M12 12l4-4-8 8z" />
    </svg>
  )
}

export function FirefoxIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FF7139">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
    </svg>
  )
}

export function EdgeIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#0078D4" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
    </svg>
  )
}

// =============================================
// Icon Mappings
// =============================================

export const SOURCE_ICONS: Record<string, React.ReactNode> = {
  x: <XIcon />,
  twitter: <XIcon />,
  'x.com': <XIcon />,
  't.co': <XIcon />,
  google: <GoogleIcon />,
  'google.com': <GoogleIcon />,
  facebook: <FacebookIcon />,
  'facebook.com': <FacebookIcon />,
  'fb.com': <FacebookIcon />,
  instagram: <InstagramIcon />,
  'instagram.com': <InstagramIcon />,
  youtube: <YouTubeIcon />,
  'youtube.com': <YouTubeIcon />,
  linkedin: <LinkedInIcon />,
  'linkedin.com': <LinkedInIcon />,
  tiktok: <TikTokIcon />,
  'tiktok.com': <TikTokIcon />,
  reddit: <RedditIcon />,
  'reddit.com': <RedditIcon />,
}

export const BROWSER_ICONS: Record<string, React.ReactNode> = {
  chrome: <ChromeIcon />,
  safari: <SafariIcon />,
  firefox: <FirefoxIcon />,
  edge: <EdgeIcon />,
  instagram: <InstagramIcon />,
  facebook: <FacebookIcon />,
}

export const OS_EMOJIS: Record<string, string> = {
  windows: '🪟',
  macos: '🍎',
  'mac os': '🍎',
  linux: '🐧',
  android: '🤖',
  ios: '📱',
  unknown: '💻',
}

export const DEVICE_EMOJIS: Record<string, string> = {
  desktop: '🖥️',
  mobile: '📱',
  tablet: '📱',
  unknown: '💻',
}

// =============================================
// Helper Functions
// =============================================

/**
 * Convert ISO country code to flag emoji
 * @param countryCode - Two-letter ISO country code (e.g., "US", "GB")
 * @returns Flag emoji (e.g., "🇺🇸", "🇬🇧")
 */
export function getCountryFlag(countryCode: string | null | undefined): string {
  if (!countryCode || countryCode.length !== 2) return '🌍'

  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0))

  return String.fromCodePoint(...codePoints)
}

/**
 * Get icon for a traffic source
 * @param source - Source name or domain
 * @returns React node (SVG icon) or emoji
 */
export function getSourceIcon(source: string | null | undefined): React.ReactNode {
  if (!source) return <span>🌐</span>

  const normalized = source.toLowerCase().replace('www.', '')

  // Check direct match
  if (SOURCE_ICONS[normalized]) {
    return SOURCE_ICONS[normalized]
  }

  // Check if source contains known platform
  for (const [key, icon] of Object.entries(SOURCE_ICONS)) {
    if (normalized.includes(key)) {
      return icon
    }
  }

  // Default icons based on common patterns
  if (normalized.includes('direct') || normalized === 'none' || normalized === '') {
    return <span>🔗</span>
  }

  if (normalized.includes('email') || normalized.includes('mail')) {
    return <span>📧</span>
  }

  if (normalized.includes('ads') || normalized.includes('campaign')) {
    return <span>📢</span>
  }

  return <span>🌐</span>
}

/**
 * Get icon for a browser
 * @param browser - Browser name
 * @returns React node (SVG icon) or emoji
 */
export function getBrowserIcon(browser: string | null | undefined): React.ReactNode {
  if (!browser) return <span>🌐</span>

  const normalized = browser.toLowerCase()

  if (BROWSER_ICONS[normalized]) {
    return BROWSER_ICONS[normalized]
  }

  // Check if browser name contains known browser
  for (const [key, icon] of Object.entries(BROWSER_ICONS)) {
    if (normalized.includes(key)) {
      return icon
    }
  }

  return <span>🌐</span>
}

/**
 * Get emoji for an operating system
 * @param os - Operating system name
 * @returns Emoji string
 */
export function getOSEmoji(os: string | null | undefined): string {
  if (!os) return '💻'

  const normalized = os.toLowerCase()

  if (OS_EMOJIS[normalized]) {
    return OS_EMOJIS[normalized]
  }

  // Check if OS name contains known OS
  for (const [key, emoji] of Object.entries(OS_EMOJIS)) {
    if (normalized.includes(key)) {
      return emoji
    }
  }

  return '💻'
}

/**
 * Get emoji for a device type
 * @param device - Device type (desktop, mobile, tablet)
 * @returns Emoji string
 */
export function getDeviceEmoji(device: string | null | undefined): string {
  if (!device) return '💻'

  const normalized = device.toLowerCase()
  return DEVICE_EMOJIS[normalized] || '💻'
}

/**
 * Get full country name from ISO code
 */
const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  AU: 'Australia',
  DE: 'Germany',
  FR: 'France',
  ES: 'Spain',
  IT: 'Italy',
  BR: 'Brazil',
  MX: 'Mexico',
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  IN: 'India',
  RU: 'Russia',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  PT: 'Portugal',
  CH: 'Switzerland',
  AT: 'Austria',
  BE: 'Belgium',
  IE: 'Ireland',
  NZ: 'New Zealand',
  SG: 'Singapore',
  HK: 'Hong Kong',
  TW: 'Taiwan',
  TH: 'Thailand',
  PH: 'Philippines',
  ID: 'Indonesia',
  MY: 'Malaysia',
  VN: 'Vietnam',
  ZA: 'South Africa',
  NG: 'Nigeria',
  EG: 'Egypt',
  AE: 'UAE',
  SA: 'Saudi Arabia',
  IL: 'Israel',
  TR: 'Turkey',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PE: 'Peru',
  VE: 'Venezuela',
  CZ: 'Czech Republic',
  HU: 'Hungary',
  RO: 'Romania',
  UA: 'Ukraine',
  GR: 'Greece',
}

export function getCountryName(countryCode: string | null | undefined): string {
  if (!countryCode) return 'Unknown'
  return COUNTRY_NAMES[countryCode.toUpperCase()] || countryCode.toUpperCase()
}
